import { Properties, Traits } from '../analyticsTypes';

// Copied from https://segment.com/docs/sources/website/analytics.js/quickstart/
// with modifications for Typescript/ES6 modules

export type SegmentAnalytics = {
  track: (event: string, properties?: Properties) => void;
  identify: (userId: string, traits?: Traits) => void;
  initialized?: boolean;
};

declare global {
  interface Window {
    analytics: SegmentAnalytics;
    accAnalyticsBaseProperties: Properties;
    SEGMENT_API_KEY: string;
    skipAlloyAnalyticsLoad?: boolean;
  }
}

/**
 * Loads Segment's Analytics.js onto the page as `window.analytics`
 *
 * @param apiKey Segment API key. If `null`, then window.analytics will be mocked.
 */
export default function loadSegment(
  apiKey: string | null,
  onLoad?: () => void,
): SegmentAnalytics {
  // Create a queue, but don't obliterate an existing one!
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const analytics: any = (window.analytics = window.analytics || ([] as any));

  // If the real analytics.js is already on the page return.
  if (analytics.initialize) return analytics;

  // If the snippet was invoked already.
  if (analytics.invoked) {
    return analytics;
  }

  // Invoked flag, to make sure the snippet
  // is never invoked twice.
  analytics.invoked = true;

  // A list of the methods in Analytics.js to stub.
  analytics.methods = [
    'trackSubmit',
    'trackClick',
    'trackLink',
    'trackForm',
    'pageview',
    'identify',
    'reset',
    'group',
    'track',
    'ready',
    'alias',
    'debug',
    'page',
    'once',
    'off',
    'on',
  ];

  // Define a factory to create stubs. These are placeholders
  // for methods in Analytics.js so that you never have to wait
  // for it to load to actually record data. The `method` is
  // stored as the first argument, so we can replay the data.
  analytics.factory = function (method: string) {
    return function () {
      // eslint-disable-next-line prefer-rest-params
      const args = Array.prototype.slice.call(arguments);
      args.unshift(method);
      analytics.push(args);
      return analytics;
    };
  };

  // For each of our methods, generate a queuing stub.
  for (const key of analytics.methods) {
    analytics[key] = analytics.factory(key);
  }

  // Define a method to load Analytics.js from our CDN,
  // and that will be sure to only ever load it once.
  analytics.load = function (key: string, options: unknown) {
    // Create an async script element based on your key.
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.fetchPriority = 'low';
    script.src =
      'https://cdn.segment.com/analytics.js/v1/' + key + '/analytics.min.js';

    script.onload = function () {
      onLoad?.();
    };

    // Insert our script next to the first script element.
    const first: HTMLScriptElement = document.getElementsByTagName('script')[0];
    // A <script> element is guaranteed to exist since the code in this file needs to be loaded somehow
    // Assuming the HTML is valid, the script node is guaranteed to have a parent
    (first.parentNode as HTMLElement).insertBefore(script, first);
    analytics._loadOptions = options;
  };

  // Add a version to keep track of what's in the wild.
  analytics.SNIPPET_VERSION = '5.2.1';

  // Load Analytics.js with your key, which will automatically
  // load the tools you've enabled for your account. Boosh!
  if (apiKey) {
    analytics.load(apiKey, {
      user: {
        localStorageFallbackDisabled: true,
      },
    });
  }

  // Make the first page call to load the integrations. If
  // you'd like to manually name or tag the page, edit or
  // move this call however you'd like.
  analytics.page();

  return analytics;
}
